import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "@/core/auth/services/authService";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useClient } from "@/features/clients/hooks/clientQueries";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { CardSetupFlow } from "../components/CardSetupFlow";
import { paymentMethodKeys } from "../hooks/paymentMethodQueries";
import { paymentMethodApiService } from "../services/paymentMethodApiService";
import { fromSetupSessionDtoToSetupSession, type SetupSession } from "../types/paymentMethodTypes";

function AddCard({ businessId, clientId, userId }: { businessId: string; clientId: string; userId: string }) {
  const capture = useWorkspaceOperation();
  const { goToClientDetails } = useAppNavigation();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SetupSession>();
  const [mode, setMode] = useState<"person" | "link">("link");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  async function start(nextMode: "person" | "link") {
    if (pending.current) return;
    const current = new AbortController();
    pending.current = current;
    const isCurrent = capture();
    setBusy(true);
    setError("");
    try {
      const result = fromSetupSessionDtoToSetupSession(
        await paymentMethodApiService.createSetupSession(businessId, clientId, current.signal));
      if (isCurrent()) { setSession(result); setMode(nextMode); }
    } catch {
      if (isCurrent()) setError("Couldn't create card setup. Check the business's Stripe connection and try again.");
    } finally {
      pending.current = null;
      if (!current.signal.aborted) {
        setBusy(false);
        if (!isCurrent()) setError("The workspace changed while preparing setup. Verify the workspace and try again.");
      }
    }
  }
  function done() {
    void queryClient.invalidateQueries({ queryKey: paymentMethodKeys.client(businessId, clientId, userId) });
    goToClientDetails(clientId, "finish");
  }
  async function copy() {
    if (!session) return;
    const isCurrent = capture();
    setError("");
    try {
      await navigator.clipboard.writeText(session.url);
      if (isCurrent()) setCopied(true);
    } catch {
      if (isCurrent()) setError("Couldn't copy the link. Select and copy it below.");
    }
  }
  return <Section title="Save a card, without charging" card>
    <p className="text-sm text-muted-foreground">The customer enters their card directly into Stripe and consents to future payments. Froppio does not store card numbers or security codes.</p>
    {!session ? <div className="flex flex-wrap gap-2">
      <Button disabled={busy} onClick={() => void start("person")}>Add card in person</Button>
      <Button variant="outline" disabled={busy} onClick={() => void start("link")}>Create secure customer link</Button>
    </div> : mode === "person" && authService.kind !== "host" ?
      <CardSetupFlow key={session.id} token={session.token} onDone={done} /> :
      <div className="space-y-4">
        <p>{mode === "person" ? "Open the secure form in your browser on this device and let the customer complete it." : "Share this private link with this customer only. They do not need a Froppio account."}</p>
        <label className="block space-y-2 text-sm">Secure card setup link
          <Input readOnly value={session.url}
            onFocus={event => event.currentTarget.select()} />
        </label>
        <p className="text-xs text-muted-foreground">Expires {session.expiresAt.toLocaleString()}. Creating a link does not save a card or record customer consent.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void copy()}>{copied ? "Link copied" : "Copy setup link"}</Button>
          <Button asChild><a href={session.url} target="_blank" rel="noopener noreferrer">Open secure form</a></Button>
          <Button variant="quiet" onClick={done}>Return to customer</Button>
        </div>
        <p role="status" className="sr-only">{copied ? "Setup link copied." : ""}</p>
      </div>}
    {busy && <LoadingSpinner message="Creating secure setup..." />}
    {error && <ErrorState message={error} />}
  </Section>;
}

export function PaymentMethodAddPage() {
  const { clientId = "" } = useParams();
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const clientQuery = useClient(clientId);
  const authorized = business && businessId && user && business.ownerId === user.id &&
    clientQuery.data?.businessId === businessId;
  return <Page>
    <meta name="referrer" content="no-referrer" />
    <PageHeader title="Add payment method" />
    <PageContent>{clientQuery.isPending ? <LoadingSpinner message="Loading customer..." /> :
      clientQuery.error ? <ErrorState message={clientQuery.error.message} onRetry={() => void clientQuery.refetch()} /> :
      authorized ? <AddCard key={`${user.id}:${businessId}:${clientId}`} businessId={businessId} clientId={clientId} userId={user.id} /> :
        <ErrorState message="Only the business owner can manage this customer's payment methods." />}
    </PageContent>
  </Page>;
}
